<script setup lang="ts">
import { useSubscription } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { computed, ref, watch } from "vue";

import { usePrintersStore } from "@/store/printers/index.js";
import {
  capitalizeFirstLetterOfEachWord,
  iconTypeForPrinters,
} from "@/utils/formatters.ts";

import CommonButton from "@/components/common/CommonButton.vue";
import FormInput from "@/components/common/FormInput.vue";

const printersStore = usePrintersStore();

const props = defineProps<{
  printers: any
  area: string
  search?: string
  loader?: boolean
}>();

const emit = defineEmits<{
  (event: "update:search", value: any): void
  (event: "use-printer", printerId: any, labelId: any): void
}>();

const search = computed({
  get() {
    return props.search;
  },
  set(value) {
    emit("update:search", value);
  },
});

const listDefaults = ref<Record<string, string>>({});

const { onResult } = useSubscription(gql`
        subscription printerMadeAsDefault {
          printerMadeAsDefault {
            id
            name
            type
            labels {
              id
              name
            }
          }
        }
    `);

const filteredPrinters = computed(() => {
  const searchQuery = props.search?.toLowerCase() || "";

  return Object.values(printersStore.printers)
    .filter((group) => props.printers[group.label.name])
    .map((group) => {
      const matchingGroup = props.printers[group.label.name];
      const defaultPrinterId = listDefaults.value[group.label.name];

      matchingGroup.printers = matchingGroup.printers.map((printer: any) => {
        if (printer.type !== props.area) return printer;

        if (printer.id == defaultPrinterId) {
          printer.is_default = true;
        }
        else if (defaultPrinterId) {
          printer.is_default = false;
        }

        return printer;
      });

      return {
        ...matchingGroup,
        printers: matchingGroup.printers.filter((printer: any) => {
          const isPrinterTitle = printer?.title?.toLowerCase()?.includes(searchQuery.toLowerCase());
          const isPrinterName = printer?.name?.toLowerCase()?.includes(searchQuery.toLowerCase());
          const isPrinterType = printer?.type?.toLowerCase()?.includes(searchQuery.toLowerCase());
          const isPrinterProgram = printer?.labels?.some((label: any) => label?.program?.toLowerCase()?.includes(searchQuery.toLowerCase()));

          return isPrinterTitle || isPrinterName || isPrinterType || isPrinterProgram;
        }).sort((a: any, b: any) => (a.id < b.id ? -1 : 1)),
      };
    })
    .filter((group) => group.printers.length > 0);
});

watch(() => props.area, () => {
  listDefaults.value = {};
});

onResult(({ data }) => {
  const printerId = data.printerMadeAsDefault.id;
  const printerType = data.printerMadeAsDefault.type.toLowerCase();
  const labelName = data.printerMadeAsDefault.labels[0].name;

  if (printerType !== props.area.toLowerCase()) return;

  listDefaults.value = {
    ...listDefaults.value,
    [labelName]: printerId,
  };
});
</script>

<template>
  <section class="flex flex-col gap-3 w-full">
    <FormInput
      v-model="search"
      placeholder="Search by location, printer name, title, or label program"
    />
    <div class="flex flex-col gap-3">
      <div
        v-for="label in filteredPrinters"
        :key="label.name"
        class="flex flex-col gap-1"
      >
        <span class="py-2 flex gap-2 items-center">
          <feather-icon :type="iconTypeForPrinters(label.type)" />
          {{ label.title || label.name || "Undefined group title" }}
        </span>
        <div class="flex flex-col gap-1">
          <div
            v-for="printer in label.printers"
            :key="printer.id"
            class="flex border-b-[1px] border-secondary justify-between p-2"
          >
            <div class="flex flex-col">
              <span class="text-[14px]">
                {{ printer.title }}
              </span>
              <span class="text-[10px]">
                The <span
                  v-if="printer.is_default"
                  class="text-primary"
                >default</span> printer {{ printer.name }} at
                {{ capitalizeFirstLetterOfEachWord(printer.type as string) }}
              </span>
            </div>
            <div>
              <span
                v-if="printer.active"
                class="text-positive"
              > In use</span>
              <CommonButton
                v-else
                text="Use"
                :loader="props.loader"
                @click="() => emit('use-printer', printer.id, label.id)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
